import React, {useState, useEffect, useContext} from 'react';
import cx from 'classnames';
import _ from 'lodash';

import Link from '../../components/Link';
import Button from '../../components/Button';
import CollapserThing from '../../components/CollapserThing';
import {Spinner} from '../../components/Spinner';
import SignupModal from '../../components/SignupModal';
import {Badge} from '../../components/Badge';
import {
  CaretIcon, Checkmark, LockIcon, VideoIcon,
} from '../../components/svg';
import {isNewLesson, isNewCourse} from '../../util';
import {useAppContext, useAnalytics} from '../../hooks';
import {CourseLessonsContext} from '../../components/CourseLessonsProvider';

/**
 * @param {array} props.lessons array of Lessons
 * @param {string} props.lessonSlug the slug of the current lesson
 * @param {boolean} props.courseIsNew whether course is new
 * @param {string|undefined} props.trackSlug the slug of the current track
 * @param {string} props.containingCourseSlug the slug of the course containing these Lessons
 * @param {function} props.onClick callback when clicking a lesson in the sidebar
 */
const LessonsList = ({
  lessons, lessonSlug, courseIsNew, trackSlug, containingCourseSlug, onClick,
}) => {
  const {hasSubscription} = useAppContext();

  const handleClick = (e, lesson) => {
    if (lesson.comingSoon) {
      e.preventDefault();
    }

    onClick(e, lesson);
  };

  return (
    <>
      {lessons.map((lesson) => {
        const lessonURL = trackSlug ? `/courses/${trackSlug}/${containingCourseSlug}/${lesson.slug}` : `/courses/${containingCourseSlug}/${lesson.slug}`;
        const isCurrentLesson = lesson.slug === lessonSlug;
        return (
          <Link
            id={`lesson_${lesson.slug}`}
            onClick={(e) => handleClick(e, lesson)}
            className={cx('flex items-center w-auto py-1.5 px-5 mr-2 font-medium rounded-r-full', {'bg-indigo-100': isCurrentLesson})}
            href={lessonURL}
            key={lessonURL}
          >
            {!lesson.free && !hasSubscription ? (
              <LockIcon className={cx('h-4 w-4 flex-shrink-0', {'text-indigo': isCurrentLesson})} />
            ) : (
              <div className={cx(
                'h-4 w-4 rounded-full flex-shrink-0 flex items-center justify-center',
                {'bg-gray-400': lesson.lastVisit},
                {'bg-indigo': isCurrentLesson},
              )}
              >
                <Checkmark className="h-2 w-2" fill="white" />
              </div>
            )}
            <span className={cx(
              'ml-2 mr-1 text-gray-700 text-sm overflow-hidden overflow-ellipsis whitespace-nowrap',
              {'text-indigo': isCurrentLesson},
            )}
            >
              {lesson.shortTitle}
            </span>
            <span className="ml-auto flex items-center">
              {lesson.comingSoon && (
                <Badge className="py-0.5 whitespace-nowrap">
                  Coming soon
                </Badge>
              )}
              {!courseIsNew && isNewLesson(lesson) && <Badge className="mr-1 py-0.5 whitespace-nowrap">New</Badge>}
              {!lesson.comingSoon && lesson.video && <VideoIcon className="w-5 text-gray-400 flex-shrink-0" />}
              {!lesson.comingSoon && lesson.difficulty && (
                <div className={cx(
                  'w-1.5 h-1.5 rounded-sm ml-1',
                  {
                    'bg-green-500': lesson.difficulty === 'Easy',
                    'bg-yellow-500': lesson.difficulty === 'Medium',
                    'bg-red-500': lesson.difficulty === 'Hard',
                  },
                )}
                />
              )}
            </span>
          </Link>
        );
      })}
    </>
  );
};

/**
 * @param {array} props.modules array of Modules
 * @param {string} props.lessonSlug the slug of the current lesson
 * @param {boolean} props.courseIsNew whether course is new
 * @param {string|undefined} props.trackSlug the slug of the current track
 * @param {string} props.containingCourseSlug the slug of the course containing these Modules
 * @param {function} props.onClick callback when clicking a lesson in the sidebar
 */
const ModulesList = ({
  modules, lessonSlug, courseIsNew, trackSlug, containingCourseSlug, onClick,
}) => {
  const [expandedModule, setExpandedModule] = useState();

  useEffect(() => {
    const moduleContainingLesson = _.find(modules,
      (module) => _.find(module.lessons, {slug: lessonSlug}));
    if (moduleContainingLesson) setExpandedModule(moduleContainingLesson.slug);
  }, [lessonSlug]);

  return (
    <>
      {modules.map((module) => {
        const isExpanded = expandedModule === module.slug;
        const isExpandable = !module.comingSoon && module.lessons?.length > 0;
        const newLessonCount = module.lessons.filter(isNewLesson).length;

        return (
          <>
            <button
              type="button"
              className="w-full py-1.5 px-5 flex justify-between items-center overflow-visible"
              onClick={() => setExpandedModule(isExpanded ? null : module.slug)}
              disabled={!isExpandable}
            >
              <h3 className="text-gray-800 text-sm font-medium whitespace-nowrap overflow-hidden overflow-ellipsis">
                {module.title}
              </h3>

              {module.comingSoon && (
                <Badge className="py-0.5 whitespace-nowrap">
                  Coming soon
                </Badge>
              )}

              {/* Don't show new lesson count if whole course is new */}
              {!courseIsNew && newLessonCount > 0 && (
                <Badge className="ml-auto mr-2 py-0.5 whitespace-nowrap">
                  {`${newLessonCount} new`}
                </Badge>
              )}

              {isExpandable && (
              <CaretIcon
                width={11}
                height={11}
                className={cx('transform transition', {'-rotate-90': isExpanded, '-rotate-180': !isExpanded})}
              />
              )}
            </button>
            <CollapserThing visible={isExpanded}>
              <LessonsList
                lessonSlug={lessonSlug}
                moduleSlug={module.slug}
                courseIsNew={courseIsNew}
                trackSlug={trackSlug}
                containingCourseSlug={containingCourseSlug}
                onClick={onClick}
                lessons={module.lessons || []}
              />
            </CollapserThing>
          </>
        );
      })}
    </>
  );
};

/**
 * @param {array} props.courses array of Courses
 * @param {string} props.lessonSlug the slug of the current lesson
 * @param {string|undefined} props.trackSlug the slug of the current track
 * @param {function} props.onClick callback when clicking a lesson in the sidebar
 */
const CoursesList = ({
  courses, lessonSlug, trackSlug, onClick,
}) => (
  courses.map((course, i) => (
    <>
      <div className="w-full px-5 mb-3 flex justify-between items-center overflow-visible">
        <h3 className="text-gray-800 text-sm font-semibold whitespace-nowrap overflow-hidden overflow-ellipsis uppercase">
          {course.shortTitle}
        </h3>
        {isNewCourse(course) && !course.comingSoon && <Badge className="mr-1 py-0.5 whitespace-nowrap">New</Badge>}

        {course.comingSoon && (
          <Badge className="py-0.5 whitespace-nowrap">Coming soon</Badge>
        )}
      </div>
      {course.available && !course.comingSoon && (
        <div>
          {course.modules?.length ? (
            <ModulesList
              lessonSlug={lessonSlug}
              courseIsNew={isNewCourse(course)}
              trackSlug={trackSlug}
              containingCourseSlug={course.slug}
              onClick={onClick}
              modules={course.modules}
            />
          ) : (
            <LessonsList
              lessonSlug={lessonSlug}
              courseIsNew={isNewCourse(course)}
              trackSlug={trackSlug}
              containingCourseSlug={course.slug}
              onClick={onClick}
              lessons={course.lessons || []}
            />
          )}
        </div>
      )}
      <div className={cx('my-6', {'border-t border-gray-200': i < courses.length - 1})} />
    </>
  ))
);

/**
 * @param {string} props.lessonSlug the slug of the current lesson
 * @param {string} props.courseSlug the slug of the current course
 * @param {string|undefined} props.trackSlug the slug of the current track
 */
const CourseSidebarContents = ({lessonSlug, courseSlug, trackSlug}) => {
  const {courses} = useContext(CourseLessonsContext);
  const {currentUser, hasSubscription} = useAppContext();
  const {tracker} = useAnalytics();
  const [trackCourse, setTrackCourse] = useState(null);
  const [isSignupOpen, setSignupOpen] = useState(false);

  useEffect(() => {
    setTrackCourse(trackSlug
      ? courses.find((c) => c.slug === trackSlug)
      : courses.find((c) => c.slug === courseSlug));
  }, [courseSlug, trackSlug]);

  useEffect(() => {
    if (trackCourse) {
      document.getElementById(`lesson_${lessonSlug}`)?.scrollIntoView({block: 'center'});
    }
  }, [trackCourse]);

  const trackSidebarUpgrade = () => {
    tracker.lessonClickSidebarUpgrade({
      courseID: courseSlug, lesson: lessonSlug, id: lessonSlug, track: trackSlug,
    });
  };

  if (!trackCourse) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const handleLessonClick = (e, lesson) => {
    tracker.lessonClickSidebar({
      courseID: courseSlug,
      id: lesson.slug,
      lesson: lesson.slug,
      currentLesson: lessonSlug,
      track: trackSlug,
    });

    if (!currentUser) {
      tracker.lessonViewSignupWall({
        course: courseSlug, lesson: lessonSlug, id: lessonSlug, track: trackSlug,
      });
      setSignupOpen(true);
      e.preventDefault();
    }
  };

  const isCourse = !trackSlug;

  return (
    <div className="mb-16">
      <SignupModal isOpen={isSignupOpen} source="lesson" />
      <div className="bg-white overflow-auto">
        <div className="py-6 px-5 mb-6 border-b border-gray-200">
          <Link href={`/courses/${trackSlug || courseSlug}`}>
            <div className="text-lg font-medium">{trackCourse.shortTitle}</div>
          </Link>
        </div>
        {isCourse && !trackCourse.modules?.length && (
          <LessonsList
            lessons={trackCourse.lessons || []}
            lessonSlug={lessonSlug}
            courseSlug={courseSlug}
            containingCourseSlug={courseSlug}
            trackSlug={trackSlug}
            onClick={handleLessonClick}
          />
        )}
        {isCourse && trackCourse.modules?.length > 0 && (
          <ModulesList
            modules={trackCourse.modules || []}
            lessonSlug={lessonSlug}
            courseSlug={courseSlug}
            containingCourseSlug={courseSlug}
            trackSlug={trackSlug}
            onClick={handleLessonClick}
          />
        )}
        {!isCourse && (
          <CoursesList
            courses={trackCourse.courses}
            lessonSlug={lessonSlug}
            courseSlug={courseSlug}
            trackSlug={trackSlug}
            onClick={handleLessonClick}
          />
        )}
      </div>
      {!hasSubscription && (
        <div
          className="self-end w-full bg-white pt-2 pb-5 px-5"
          style={{boxShadow: 'white 0px -30px 30px -10px'}}
        >
          <Button
            href={`/upgrade?course=${courseSlug}&src=course-sidebar`}
            onClick={trackSidebarUpgrade}
            asLink
            className="block"
          >
            Unlock full course
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * @param {string} props.lessonSlug the slug of the current lesson
 * @param {string} props.courseSlug the slug of the current course
 * @param {string|undefined} props.trackSlug the slug of the current track
 */
const CourseSidebar = ({lessonSlug, courseSlug, trackSlug}) => (
  <>
    <div className="hidden lg:block">
      <div className="flex-col justify-between bg-white max-h-full lg:flex">
        <CourseSidebarContents
          lessonSlug={lessonSlug}
          courseSlug={courseSlug}
          trackSlug={trackSlug}
        />
      </div>
    </div>
    <div className="block lg:hidden">
      <CourseSidebarContents
        lessonSlug={lessonSlug}
        courseSlug={courseSlug}
        trackSlug={trackSlug}
      />
    </div>
  </>
);

export default CourseSidebar;
